import React, { useCallback, useEffect, useId, useRef, useState } from 'react'
import { HexColorPicker } from 'react-colorful'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { useTranslation } from '../hooks/use-i18n'

/** Ensure react-colorful always receives a valid #rrggbb string. */
export function normalizeArenaHex(input: string, fallback: string): string {
  let s = input.trim()
  if (!s.startsWith('#')) s = `#${s}`
  const full = /^#([0-9A-Fa-f]{6})$/.exec(s)
  if (full) return `#${full[1].toLowerCase()}`
  const short = /^#([0-9A-Fa-f]{3})$/.exec(s)
  if (short) {
    const c = short[1]
    return `#${c[0]}${c[0]}${c[1]}${c[1]}${c[2]}${c[2]}`.toLowerCase()
  }
  return fallback
}

interface ArenaColorPickerFieldProps {
  label: React.ReactNode
  value: string
  onChange: (hex: string) => void
  defaultHex: string
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

const ArenaColorPickerField: React.FC<ArenaColorPickerFieldProps> = ({
  label,
  value,
  onChange,
  defaultHex,
  isOpen,
  onOpenChange,
}) => {
  const { t } = useTranslation()
  const panelRef = useRef<HTMLDivElement>(null)
  const stableDefault = normalizeArenaHex(defaultHex, '#000000')
  const pickerValue = normalizeArenaHex(value, stableDefault)
  const id = useId()
  const panelId = `${id}-panel`
  const [showHex, setShowHex] = useState(false)
  const [hexDraft, setHexDraft] = useState(pickerValue)

  useEffect(() => {
    if (showHex) setHexDraft(pickerValue)
  }, [pickerValue, showHex])

  const close = useCallback(() => onOpenChange(false), [onOpenChange])

  useEffect(() => {
    if (!isOpen) {
      setShowHex(false)
      return
    }
    panelRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isOpen, close])

  const applyHexDraft = () => {
    const next = normalizeArenaHex(hexDraft, pickerValue)
    onChange(next)
    setHexDraft(next)
  }

  const swatchId = `${id}-swatch`

  return (
    <div className="min-w-0">
      <label htmlFor={swatchId} className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <button
          type="button"
          id={swatchId}
          className="h-10 w-10 shrink-0 rounded-lg border-2 border-border shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-2 focus:ring-offset-background"
          style={{ backgroundColor: pickerValue }}
          aria-expanded={isOpen}
          aria-controls={panelId}
          title={t('createRoom.arenaColorOpenPicker')}
          onClick={() => onOpenChange(!isOpen)}
        />
        <button
          type="button"
          className="flex-1 min-w-0 rounded-lg border border-border bg-background px-3 py-2 text-left text-small text-foreground hover:bg-muted/50 transition-colors"
          onClick={() => onOpenChange(!isOpen)}
        >
          {isOpen ? t('createRoom.arenaColorClosePicker') : t('createRoom.arenaColorOpenPicker')}
        </button>
      </div>

      {isOpen && (
        <div
          ref={panelRef}
          id={panelId}
          role="region"
          aria-label={typeof label === 'string' ? label : t('createRoom.arenaColorOpenPicker')}
          className="mt-2 rounded-xl border border-border bg-card p-3 shadow-sm ring-1 ring-black/5 dark:ring-white/10"
        >
          <div className="mx-auto max-w-[240px] [&_.react-colorful]:w-full [&_.react-colorful]:max-w-full [&_.react-colorful__saturation]:rounded-lg [&_.react-colorful__hue]:h-3 [&_.react-colorful__hue]:rounded [&_.react-colorful__hue-pointer]:w-4 [&_.react-colorful__hue-pointer]:h-4">
            <HexColorPicker color={pickerValue} onChange={onChange} />
          </div>
          <div className="mt-3">
            <button
              type="button"
              className="text-xs font-medium text-primary hover:underline"
              onClick={() => {
                onChange(stableDefault)
              }}
            >
              {t('createRoom.arenaColorResetDefault')}
            </button>
          </div>
          <div className="mt-2 border-t border-border pt-2">
            <button
              type="button"
              className="flex w-full items-center justify-between text-xs font-medium text-muted-foreground hover:text-foreground"
              onClick={() => setShowHex((s) => !s)}
            >
              {t('createRoom.arenaColorHexOptional')}
              {showHex ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
            {showHex && (
              <div className="mt-2 flex gap-2">
                <input
                  type="text"
                  value={hexDraft}
                  onChange={(e) => setHexDraft(e.target.value)}
                  onBlur={applyHexDraft}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      applyHexDraft()
                    }
                  }}
                  className="min-w-0 flex-1 rounded-lg border border-border bg-background px-2 py-1.5 font-mono text-xs"
                  placeholder="#3b9b78"
                  spellCheck={false}
                  autoComplete="off"
                  aria-label={t('createRoom.arenaColorHexOptional')}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default ArenaColorPickerField
